import { getProxyUrl } from '@proxies/index'
import { ItemWithOptions } from '@types'
import { API } from '@utils/queryBuilder'
import { safeParseJsonResponse } from '@utils/sceneObjects/linked/textureCatalogApi'

export type LinkedUnitOptionsBucket = Record<string, ItemWithOptions[] | undefined>

export type LinkedUnitOptionsPatch = {
  tiradorOptions?: ItemWithOptions[]
  grifoOptions?: ItemWithOptions[]
  fregaderoOptions?: ItemWithOptions[]
  encimeraOptions?: ItemWithOptions[]
  placaOptions?: ItemWithOptions[]
  zocaloOptions?: ItemWithOptions[]
}

const responseCache = new Map<number, Record<string, unknown>>()
const inFlight = new Map<number, Promise<Record<string, unknown>>>()

export function clearLinkedUnitOptionsCache(): void {
  responseCache.clear()
  inFlight.clear()
}

export function extractLinkedOptionsBucket(
  payload: Record<string, unknown>,
): LinkedUnitOptionsBucket {
  const bucket = (payload['0'] ?? payload) as LinkedUnitOptionsBucket
  return bucket ?? {}
}

export function mapLinkedUnitOptionsResponse(
  payload: Record<string, unknown>,
): LinkedUnitOptionsPatch {
  const bucket = extractLinkedOptionsBucket(payload)
  return {
    tiradorOptions: bucket.Tirador,
    grifoOptions: bucket.TPCGrifo,
    fregaderoOptions: bucket.Fregadero,
    encimeraOptions: bucket.Encimera,
    placaOptions: bucket.Placa,
    zocaloOptions: bucket.Zocalo,
  }
}

function preserveOptions<T>(next: T[] | undefined, existing: T[] | undefined): T[] | undefined {
  if (!next || next.length === 0) return existing
  return next
}

/** Aplica opciones enlazadas al item en memoria (sin set de store). */
export function applyLinkedUnitOptionsPatch(
  item: {
    tiradorOptions?: ItemWithOptions[]
    grifoOptions?: ItemWithOptions[]
    fregaderoOptions?: ItemWithOptions[]
    encimeraOptions?: ItemWithOptions[]
    placaOptions?: ItemWithOptions[]
    zocaloOptions?: ItemWithOptions[]
  },
  patch: LinkedUnitOptionsPatch,
  options?: { preserveExisting?: boolean; clearTirador?: boolean },
): void {
  const keep = <T>(next?: T[], existing?: T[]) =>
    options?.preserveExisting ? preserveOptions(next, existing) : next

  if (options?.clearTirador) {
    item.tiradorOptions = []
  } else {
    const tirador = keep(patch.tiradorOptions, item.tiradorOptions)
    if (tirador) item.tiradorOptions = tirador
  }

  const grifo = keep(patch.grifoOptions, item.grifoOptions)
  if (grifo) item.grifoOptions = grifo

  const fregadero = keep(patch.fregaderoOptions, item.fregaderoOptions)
  if (fregadero) item.fregaderoOptions = fregadero

  const encimera = keep(patch.encimeraOptions, item.encimeraOptions)
  if (encimera) item.encimeraOptions = encimera

  const placa = keep(patch.placaOptions, item.placaOptions)
  if (placa) item.placaOptions = placa

  const zocalo = keep(patch.zocaloOptions, item.zocaloOptions)
  if (zocalo) item.zocaloOptions = zocalo
}

/**
 * Una sola petición por idUnidades; deduplica llamadas concurrentes e in-flight.
 * Usar clearLinkedUnitOptionsCache() al cambiar de unidad.
 */
export async function fetchLinkedUnitOptions(
  idUnidades: number,
): Promise<Record<string, unknown>> {
  const cached = responseCache.get(idUnidades)
  if (cached) return cached

  const pending = inFlight.get(idUnidades)
  if (pending) return pending

  const request = (async () => {
    try {
      const response = await API().api.acabadosGetElementosEnlazadosUnidadAcabados(idUnidades)
      const data = ((response as { data?: unknown }).data ?? response) as Record<string, unknown>
      const normalized = data && typeof data === 'object' ? data : {}
      responseCache.set(idUnidades, normalized)
      return normalized
    } finally {
      inFlight.delete(idUnidades)
    }
  })()

  inFlight.set(idUnidades, request)
  return request
}

/** Variante fetch directo (p. ej. updateLinkedItemGola antes de API client). */
export async function fetchLinkedUnitOptionsRaw(
  idUnidades: number,
): Promise<Record<string, unknown> | null> {
  const cached = responseCache.get(idUnidades)
  if (cached) return cached

  const pending = inFlight.get(idUnidades)
  if (pending) return pending

  const request = (async () => {
    try {
      const response = await fetch(
        getProxyUrl(`api/Acabados/getelementosenlazadosunidadacabados/${idUnidades}`),
        {
          method: 'GET',
          mode: 'cors',
          headers: {
            'Accept-Language': 'ES',
            'Content-Type': 'application/json',
            Authorization: 'Ninpo ' + sessionStorage.getItem('access_token'),
          },
        },
      )

      if (!response.ok) return null

      const json = await safeParseJsonResponse(response)
      const normalized =
        json && typeof json === 'object' ? (json as Record<string, unknown>) : null
      if (normalized) responseCache.set(idUnidades, normalized)
      return normalized
    } finally {
      inFlight.delete(idUnidades)
    }
  })()

  inFlight.set(idUnidades, request)
  return request
}
